// Local storage utility for managing resume submissions
export interface StoredResume {
    id: string;
    name: string;
    email: string;
    phone: string;
    location: string;
    currentRole: string;
    experience: string;
    education: string;
    summary: string;
    resumeUrl: string; // base64 data URL
    resumeFileName: string;
    resumeFileType: string;
    linkedIn?: string;
    portfolio?: string;
    status: 'new' | 'reviewed' | 'contacted' | 'archived';
    visibility?: 'visible' | 'hidden';
    submittedDate: string;
    uploadedDate?: string; // Alias for submittedDate (for MongoDB compatibility)
    rating?: number; // Rating from admin
    skills: string[];
    certifications?: string[];
    // Additional fields from form
    gender?: string;
    dob?: string;
    currentAddress?: string;
    qualification?: string;
    specialization?: string;
    university?: string;
    yearOfPassing?: string;
    percentageCgpa?: string;
    totalExperience?: string;
    previousJobTitle?: string;
    currentCtc?: string;
    expectedSalary?: string;
    noticePeriod?: string;
    preferredLocation?: string;
    willingToRelocate?: string;
    employmentType?: string;
}

const STORAGE_KEY = 'amasqis_resumes';
const MAX_STORAGE_SIZE = 4 * 1024 * 1024; // 4MB safety limit

// Helper to check storage size
const getStorageSize = (): number => {
    let total = 0;
    for (let key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
            total += localStorage[key].length + key.length;
        }
    }
    return total;
};

// Helper to sanitize resume data before storage (remove large Base64 data)
const sanitizeForStorage = (resume: StoredResume): StoredResume => {
    const sanitized = { ...resume };
    
    // If resumeUrl is Base64 data (starts with data:), replace with metadata only
    if (sanitized.resumeUrl && sanitized.resumeUrl.startsWith('data:')) {
        sanitized.resumeUrl = `[Resume uploaded: ${sanitized.resumeFileName || 'resume.pdf'}]`;
    }
    
    return sanitized;
};

export const resumeStorage = {
    // Get all resumes
    getAll(): StoredResume[] {
        try {
            const data = localStorage.getItem(STORAGE_KEY);
            return data ? JSON.parse(data) : [];
        } catch (error) {
            console.error('Error reading resumes from localStorage:', error);
            return [];
        }
    },

    // Add new resume
    add(resume: Omit<StoredResume, 'id' | 'status' | 'submittedDate'>): StoredResume {
        try {
            const resumes = this.getAll();
            const timestamp = new Date().toISOString();
            const newResume: StoredResume = {
                ...resume,
                id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
                status: 'new',
                submittedDate: timestamp,
                uploadedDate: timestamp, // Set both fields
            };
            
            // Sanitize the resume to remove Base64 data
            const sanitizedResume = sanitizeForStorage(newResume);
            
            resumes.unshift(sanitizedResume); // Add to beginning
            
            // Check storage size before attempting to store
            const dataToStore = JSON.stringify(resumes);
            const currentSize = getStorageSize();
            const newDataSize = dataToStore.length;
            
            if (currentSize + newDataSize > MAX_STORAGE_SIZE) {
                console.warn('localStorage quota approaching limit. Removing oldest resumes.');
                // Keep only the most recent 50 resumes
                const trimmedResumes = resumes.slice(0, 50);
                localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedResumes));
                console.log('✅ Stored resume (after trimming old data)');
            } else {
                localStorage.setItem(STORAGE_KEY, dataToStore);
                console.log('✅ Stored resume in localStorage');
            }
            
            return newResume; // Return unsanitized version for immediate use
        } catch (error) {
            // Handle QuotaExceededError specifically
            if (error instanceof Error && error.name === 'QuotaExceededError') {
                console.error('❌ localStorage quota exceeded. Resume NOT stored locally (but still submitted to server)');
                // Return the resume anyway so the form submission can continue
                const timestamp = new Date().toISOString();
                return {
                    ...resume,
                    id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
                    status: 'new',
                    submittedDate: timestamp,
                    uploadedDate: timestamp,
                };
            }
            console.error('Error adding resume to localStorage:', error);
            throw error;
        }
    },

    // Update resume
    update(id: string, updates: Partial<StoredResume>): StoredResume | null {
        try {
            const resumes = this.getAll();
            const index = resumes.findIndex(r => r.id === id);
            if (index === -1) return null;
            
            resumes[index] = { ...resumes[index], ...updates };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(resumes));
            return resumes[index];
        } catch (error) {
            console.error('Error updating resume:', error);
            return null;
        }
    },

    // Delete resume
    delete(id: string): boolean {
        try {
            const resumes = this.getAll();
            const filtered = resumes.filter(r => r.id !== id);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
            return true;
        } catch (error) {
            console.error('Error deleting resume:', error);
            return false;
        }
    },

    // Get single resume
    getById(id: string): StoredResume | null {
        const resumes = this.getAll();
        return resumes.find(r => r.id === id) || null;
    },

    // Clear all resumes (useful for testing)
    clear(): void {
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (error) {
            console.error('Error clearing resumes:', error);
        }
    }
};

// Helper function to convert file to base64
export const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = error => reject(error);
    });
};

// Helper function to download base64 file
export const downloadBase64File = (base64Data: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = base64Data;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};